import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";

export interface CalendarPreferences {
  activeCalendars: Record<string, boolean>;
}

const STORAGE_KEY = "calendarPreferences";
const DEFAULT_ACTIVE_STATE = true;

const loadPreferencesFromStorage = (): CalendarPreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { activeCalendars: {} };
  } catch {
    return { activeCalendars: {} };
  }
};

const initialState: CalendarPreferences = loadPreferencesFromStorage();

export const calendarPreferencesSlice = createSlice({
  name: "calendarPreferences",
  initialState,
  reducers: {
    toggleCalendar: (state, action: PayloadAction<string>) => {
      const calendarId = action.payload;
      state.activeCalendars[calendarId] = !state.activeCalendars[calendarId];
    },
    setCalendarActive: (
      state,
      action: PayloadAction<{ calendarId: string; active: boolean }>,
    ) => {
      const { calendarId, active } = action.payload;
      state.activeCalendars[calendarId] = active;
    },
    setAllCalendarsActive: (state, action: PayloadAction<boolean>) => {
      const active = action.payload;
      // Get all calendar IDs from the current state
      const calendarIds = Object.keys(state.activeCalendars);
      calendarIds.forEach((id) => {
        state.activeCalendars[id] = active;
      });
    },
    initializeCalendars: (state, action: PayloadAction<string[]>) => {
      const calendarIds = action.payload;
      calendarIds.forEach((id) => {
        if (!(id in state.activeCalendars)) {
          state.activeCalendars[id] = DEFAULT_ACTIVE_STATE;
        }
      });
    },
  },
});

export const {
  toggleCalendar,
  setCalendarActive,
  setAllCalendarsActive,
  initializeCalendars,
} = calendarPreferencesSlice.actions;

export const selectActiveCalendars = (state: RootState) =>
  state.calendarPreferences.activeCalendars;

export const selectIsCalendarActive = (calendarId: string) => (state: RootState) =>
  state.calendarPreferences.activeCalendars[calendarId] ?? DEFAULT_ACTIVE_STATE;

export default calendarPreferencesSlice.reducer;
